import React, { FC, ReactElement, useMemo } from 'react';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import { getCounterType, getSportsNavTopic } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.utils.ts';
import { UseSportsNavStore, useSportsNavList, NavTopicStoreCreatorProps, useNavTopicStoreCreator } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.hooks.ts';
import { SportsNavType, SportsNavCounterType } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.constants';
import { DisciplineLiveStatus, DisciplineVirtualStatus, MatchDetailsViewModel, SportCounterType } from '@om-fe/models';
import MatchListAllItem from './match-list-all-item';
import { bem } from '@om-fe/core/components/live-event/sidebar/sports-sidebar/live-event-sidebar';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { RouteComponentProps, withRouter } from 'react-router';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { getLiveEventSidebarMatchListConfig } from '@om-fe/core/components/match-details/utils/match-details.utils';
import FavoriteMatchList from '../match-list/containers/favorite-match-list/favorite-match-list';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { LiveEventRouteParams } from '@om-fe/core/core/routing/route-params';
import { DEFAULT_DISPLAYED_NUMBER_OF_MATCHES } from '../match-list/utils/match-list.constants';

interface MatchListAllPublicProps {
    readonly hasFavoriteMatches?: boolean;
    readonly expandedDisciplines?: string[];
}

interface MatchListAllStoreProps {
    readonly userID?: number;
    readonly event?: MatchDetailsViewModel;
    readonly isLiveSports: boolean;
    readonly breakpoints: BreakpointsState | null;
    readonly showOnlyTournamentHeader?: boolean;
    readonly isAuthenForTopic?: boolean;
}

type MatchListAllProps = MatchListAllStoreProps
    & RouteComponentProps<LiveEventRouteParams>
    & MatchListAllPublicProps;

const MatchListAllComponent: FC<MatchListAllProps> = (
    {
        userID,
        match,
        hasFavoriteMatches = false,
        expandedDisciplines,
        isLiveSports,
        breakpoints,
        showOnlyTournamentHeader = false,
        isAuthenForTopic,
    }
): ReactElement => {
    const { params: { id: matchId, disciplineId: disciplineUrlId }} = match;

    const topic: string = getSportsNavTopic(SportsNavType.Upcoming);
    const liveCounterType: SportCounterType | undefined = getCounterType(
        SportsNavType.Live,
        true,
        SportsNavCounterType.NumberOfEvents
    );
    const upcomingCounterType: SportCounterType | undefined = getCounterType(
        SportsNavType.Upcoming,
        false,
        SportsNavCounterType.NumberOfEvents
    );

    const topicDetails = useMemo((): NavTopicStoreCreatorProps => ({
        liveStatus: DisciplineLiveStatus.LIVE,
        virtualStatus: DisciplineVirtualStatus.NOT_VIRTUAL,
        liveCounterType,
        upcomingCounterType,
    }), [liveCounterType, upcomingCounterType])

    const useStore: UseSportsNavStore = useNavTopicStoreCreator(
        topic,
        topicDetails,
    );

    const list: string[] = useSportsNavList(useStore);

    const { numberOfOddsExpanded } = getLiveEventSidebarMatchListConfig();
    const config: OMFE2ConfigModel = getConfig();
    const matchListLayout = config.matchListConfig?.commonConfig?.matchListItemLayout;
    const eventsNumber: number = config.matchListConfig?.commonConfig?.eventsNumber ?? DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;

    return (
        <>
            {hasFavoriteMatches && !!userID && <FavoriteMatchList
                activeMatchId={matchId || null}
                userID={userID}
                instanceId={`LiveEventSidebarFavorites`}
                liveStatus={DisciplineLiveStatus.LIVE}
                extraClassName={bem('MatchList')}
                routeName={RouteNames.liveEvent}
                numberOfOddsExpanded={numberOfOddsExpanded}
                limitNumberOfEvents={eventsNumber}
                showMixedMatchListHeader
                eventItemExtraClassname={bem({
                    element: 'EventItem',
                    extra: 'LiveEventItem',
                })}
                itemLayout={matchListLayout}
                hasHeader
                showMatchListHeader={false}
                showMarketInMatchListGroupHeader={true}
            />}
            {list.map((disciplineId, idx) => {
                let initialIsExpanded: boolean;

                if (expandedDisciplines) {
                    initialIsExpanded = expandedDisciplines.some(id => id === disciplineId);
                } else {
                    initialIsExpanded = disciplineUrlId === disciplineId;
                }

                return (
                    <MatchListAllItem
                        activeMatchId={matchId || null}
                        key={disciplineId}
                        useStore={useStore}
                        sportId={disciplineId}
                        initialIsExpanded={initialIsExpanded}
                        instanceId={idx}
                        isLiveSports={isLiveSports}
                        breakpoints={breakpoints}
                        showOnlyTournamentHeader={showOnlyTournamentHeader}
                        isAuthenForTopic={isAuthenForTopic}
                    />
                )
            })}
        </>
    );
};

const MatchListAll: React.ComponentType = compose(
    withRouter,
    connect<MatchListAllStoreProps>(
        () => {
            return (state: AppState): MatchListAllStoreProps => {
                const { session: { info }, routing } = state;
                const breakpoints: BreakpointsState | null = state.breakpoints;
                const userID: number | undefined = info && info.userID;

                const isLiveSports: boolean = routing.name === RouteNames.liveSports;
                const { enabledTopicDelay } = getConfig();
                const isAuthenForTopic: boolean = !!(enabledTopicDelay && state.session?.info?.isAuthenticated);

                return {
                    userID,
                    isLiveSports,
                    breakpoints,
                    isAuthenForTopic,
                };
            };
        },
    ),
)(MatchListAllComponent);

export default MatchListAll;
